/**
 * Unified SurveyForm component for AD projects.
 *
 * Supports three usage modes:
 * 1. Pass a pre-built SurveyJS Model via `survey` prop (Site.V2 CaptureForms pattern)
 * 2. Pass a `surveyCode` + `apiGet` function to fetch and build the model (Auth pattern)
 * 3. Pass a `formId` + `apiGet` function to fetch from "forms" controller (Site.V2 inline forms pattern)
 *
 * Props:
 * - survey (Model)         — Pre-built SurveyJS model. If provided, surveyCode/formId are ignored.
 * - surveyCode (string)    — Code to fetch form JSON from "account" controller. Requires apiGet.
 * - formId (string|number) — Form ID to fetch form JSON from "forms" controller. Requires apiGet.
 * - apiGet (function)      — API function to fetch form data. Signature: apiGet(props) => Promise<response>
 * - controller (string)    — API controller name to use for fetching (default: auto-detected based on mode)
 * - environment (string)   — Environment identifier (optional, falls back to useParams)
 * - onComplete (function)  — Callback when survey is completed
 * - enableBooleanSlider (bool) — Whether to register/handle boolean toggle-switch (default: true)
 * - enableEndpoints (bool) — Whether to handle dynamic data endpoint loading (default: true)
 * - dropdownFallbackLabel (string) — Fallback label for ungrouped dropdown items (default: "Other")
 * - hideFirstPagePanel (bool) — When true, hides the first PAGE-level title and description (used on Register page where native fields precede the survey section). Section/panel titles are preserved. (default: false)
 * - enablePaging (bool) — When true, preserves multiple pages from the backend and fires onPageChanged when the user navigates. When false (default), collapses to a single page for backward compatibility (Register, legacy forms). (default: false)
 * - onPageChanged (function) — Callback fired when the visible page changes (only when enablePaging=true). Receives the new 0-based page index. Used by the host component (e.g. CaptureForms) to track the current page for RHC rendering.
 * - onReady (function) — Callback fired once the SurveyJS model has been built (Mode 2/3 fetch, or Mode 1 pre-built). Receives { pageCount, currentPageNo, hasBranching, firstPageTitle, firstPageDescription }. firstPageTitle/firstPageDescription are the first page's admin-configured title and description captured before hideFirstPagePanel clears them (empty strings if none). Lets a host that lets SurveyForm build the model internally (e.g. the Register page) learn the initial page count without reaching into the model. Optional.
 * - branchingRules (object) — Conditional-navigation map (PADD-3742) keyed by SurveyJS question name. When non-empty, the imperative nextPage()/prevPage() route through a BranchingEngine that can jump pages or complete the form based on the selected answer. When null/empty (the default), navigation is purely linear and identical to before. In Mode 2/3 (SurveyForm fetches the form itself) this can be omitted — the engine is auto-wired from response.data.branchingRules. (default: null)
 */
import React, { forwardRef, useRef, useImperativeHandle, useEffect, useState } from "react";
import { Model, QuestionTextModel, getLocaleStrings, settings } from "survey-core";
import { Survey } from "survey-react-ui";
import DOMPurify from 'dompurify';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Branching engine (PADD-3742) — client-side conditional page navigation.
import BranchingEngine from "./BranchingEngine";

// Import side-effect modules that register serializers and renderers
import "./serializers";
import "./CustomSelect";
import "./BooleanSlider";
// AddressFieldsFromCountry is registered lazily at runtime (see the register call below)
// to avoid module init-order issues in the bundled/production build.
import { registerAddressFieldsFromCountry, expandAddressFieldsFromCountry, prefillStandaloneAddressFields } from "./AddressFieldsFromCountry";
import "./OtherGroupRenderer";
import "./DateDropdowns";
// Step indicator (PADD-3866) — registers the "stepindicator" custom element.
import { STEP_INDICATOR_TYPE } from "./StepIndicatorElement";

// Import styles
import "survey-core/survey-core.min.css";
import "../styles/resetSurvey.scss";

// Register custom "cssClass" property so SurveyJS preserves it when parsing JSON
import { Serializer } from "survey-core";
if (!Serializer.findProperty("question", "cssClass")) {
    Serializer.addProperty("question", { name: "cssClass", default: "" });
}
// Register on "panel" too so section-level cssClass survives JSON parsing
if (!Serializer.findProperty("panel", "cssClass")) {
    Serializer.addProperty("panel", { name: "cssClass", default: "" });
}

// Promote SurveyJS heading tags for a correct document outline / accessibility.
// By default SurveyJS renders the page title, section (panel) title and question title
// all as plain <h4> nodes. `settings.titleTags` tells SurveyJS to emit the right tag
// directly at render time, so no post-render DOM swapping is needed:
//   page title     → <h1>
//   section title  → <h2>  (panel)
//   question title → <h4>  (unchanged default)
// (We don't render a survey-level title, so "survey" is left at its default.)
settings.titleTags = {
    ...settings.titleTags,
    page: "h1",
    panel: "h2",
    question: "h4"
};

// PADD-3866: injects one step-indicator element at the top of every page so SurveyJS
// renders it after each page's title/description. Only runs for genuinely multi-page
// forms; branching forms are handled by the caller (which skips injection entirely).
// Idempotent — never adds a second indicator to a page.
function injectStepIndicators(surveyModel, startPage = 0) {
    if (!surveyModel || !Array.isArray(surveyModel.pages)) return;
    if (surveyModel.pageCount <= 1) return;
    // Changing page clears any error flag so the indicator doesn't keep a stale red node.
    surveyModel.onCurrentPageChanged.add((sender) => {
        sender.getAllQuestions().forEach((q) => {
            if (q.getType && q.getType() === STEP_INDICATOR_TYPE
                && q.getPropertyValue("adErrorStep") !== null
                && q.getPropertyValue("adErrorStep") !== undefined) {
                q.setPropertyValue("adErrorStep", null);
            }
        });
    });
    surveyModel.pages.forEach((page, pageIndex) => {
        // Skip pages before startPage. The Register page renders its own indicator under
        // the "Create your account" heading for page 0 (above the native fields, where an
        // in-survey element can't reach), so it injects from page 1 onward instead.
        if (pageIndex < startPage) return;
        const already = (page.elements || []).some(
            (el) => el.getType && el.getType() === STEP_INDICATOR_TYPE
        );
        if (already) return;
        const indicator = page.addNewQuestion(STEP_INDICATOR_TYPE, `__stepIndicator_${pageIndex}`);
        if (indicator) {
            // Suppress the auto-generated title ("__stepIndicator_N") and the "Optional"
            // marker — hiding the title location removes the whole question header, leaving
            // just the indicator widget.
            indicator.title = "";
            indicator.titleLocation = "hidden";
            indicator.description = "";
            indicator.hideNumber = true;
            // Move it to the very top of the page so it sits directly under the page header.
            if (typeof page.removeElement === "function") {
                page.removeElement(indicator);
                page.addElement(indicator, 0);
            }
        }
    });
}

// Build a lookup map of question name → cssClass from raw survey JSON
function buildCssClassMap(surveyModel) {
    const map = {};
    if (!surveyModel) return map;
    try {
        const json = surveyModel.toJSON();
        if (json?.pages) {
            json.pages.forEach(page => {
                (page.elements || []).forEach(function walkElement(el) {
                    if (el.cssClass) {
                        map[el.name] = el.cssClass;
                    }
                    // Handle panels with nested elements
                    if (el.elements) {
                        el.elements.forEach(walkElement);
                    }
                });
            });
        }
    } catch (e) { /* ignore */ }
    return map;
}

// Collapses a multi-page definition to a single page by removing every page after the
// first. Used when paging is disabled (the default) so legacy single-page consumers
// (Register, inline capture forms without pages) behave exactly as before. Any extra
// pages defined in the backend form definition are intentionally dropped.
function keepFirstPageOnly(surveyModel) {
    if (!surveyModel || !Array.isArray(surveyModel.pages)) return;
    // Remove pages from the end so indices stay valid while iterating.
    for (let i = surveyModel.pages.length - 1; i >= 1; i--) {
        surveyModel.removePage(surveyModel.pages[i]);
    }
}

// Append the display-layout class to a question's root wrapper via
// onUpdateQuestionCssClasses so it persists through React re-renders.
// The backend emits `displayColumn` (1 or 2) only for radio, checkbox and slider-toggle
// questions (never boolean). Radio maps to a "radiogroup" question; both checkbox and
// slider-toggle map to a "checkbox" question. 1-column is the default layout so no class
// is added for it; only the 2-column case emits `two-layout-col`.
function applyDisplayLayoutClass(options) {
    const type = options.question.getType();
    if (type !== 'radiogroup' && type !== 'checkbox') return;
    const displayColumn = options.question.jsonObj?.displayColumn
        ?? options.question.getPropertyValue("displayColumn");
    // 1-column is the default layout, so only the 2-column class needs to be emitted.
    if (Number(displayColumn) === 2) {
        options.cssClasses.mainRoot = (options.cssClasses.mainRoot || "") + " two-layout-col";
    }
}



const SurveyForm = forwardRef(({
    survey,
    surveyCode,
    formId,
    apiGet,
    controller: controllerProp,
    environment: environmentProp,
    onComplete,
    enableBooleanSlider = true,
    enableEndpoints = true,
    dropdownFallbackLabel,
    hideFirstPagePanel = false,
    enablePaging = false,
    onPageChanged,
    onReady,
    branchingRules = null,
    
    // for getPartyAddress call, user token is needed in the data.
    userToken = null,
    // Opt-in (set by the consumer, e.g. CaptureForms) to pre-fill the authenticated party's
    // latest address into the "addressFieldsFromCountry" qaddress component via getpartyaddress.
    // Stashed on the model as adPrefillLatestAddress and read by expandAddressFieldsFromCountry
    // (qaddress block) and prefillStandaloneAddressFields (override-mode standalone fields).
    // Off by default so other consumers (e.g. Register) are unaffected.
    prefillLatestAddress = false,
    // PADD-3866: when true (default) and paging is on, a step indicator is injected into
    // each page of a multi-page, non-branching form. Hosts that render their own indicator
    // outside the survey (e.g. the Register page, which shows it under the account heading
    // above the native fields) pass false to opt out.
    showStepIndicator = true,
    // PADD-3866: first page index that gets an in-survey step indicator. Register renders
    // page 0's indicator itself (under the account heading) and passes 1 so the in-survey
    // indicator is injected from page 1 onward, sitting under each survey page's title.
    stepIndicatorStartPage = 0
}, ref) => {
    // Register the custom "addressFieldsFromCountry" question at runtime (idempotent).
    // Runs before any model parse so SurveyJS recognises the element.
    registerAddressFieldsFromCountry();

    const [surveyData, setSurveyData] = useState(survey || null);
    const cssClassMapRef = useRef({});
    // Branching engine (PADD-3742). Holds a BranchingEngine instance once the model is built
    // AND branching rules exist; stays null for non-branching forms so navigation is linear.
    const branchingEngineRef = useRef(null);
    // The first page's admin-configured title & description, captured before hideFirstPagePanel
    // clears them. Surfaced to the host via onReady so a multi-page Register page can use them
    // as its heading and intro text.
    const firstPageTitleRef = useRef('');
    const firstPageDescriptionRef = useRef('');
    const params = useParams();
    const environment = environmentProp || params?.environment;
    const { t } = useTranslation();
    const initialized = useRef(false);
    const fallbackLabel = dropdownFallbackLabel || t('register.dropdown', 'Other');

    // Override SurveyJS built-in UI strings with values from the Languages table
    const enLocale = getLocaleStrings("en");
    enLocale.optionsCaption = t('surveyjs.optionsCaption');
    enLocale.otherItemText = t('surveyjs.otherItemText');
    enLocale.noneItemText = t('surveyjs.noneItemText');
    enLocale.selectAllItemText = t('surveyjs.selectAllItemText');
    enLocale.requiredError = t('surveyjs.requiredError');
    enLocale.requiredInAllRowsError = t('surveyjs.requiredInAllRowsError');
    enLocale.numericError = t('surveyjs.numericError');
    enLocale.textMinLength = t('surveyjs.textMinLength');
    enLocale.textMaxLength = t('surveyjs.textMaxLength');
    enLocale.completingSurvey = t('surveyjs.completingSurvey');
    enLocale.loadingSurvey = t('surveyjs.loadingSurvey');
    enLocale.clearCaption = t('surveyjs.clearCaption');
    enLocale.chooseFileCaption = t('surveyjs.chooseFileCaption');

    // Determine the controller to use based on mode:
    // - formId provided → "forms" controller (Site.V2 inline forms pattern)
    // - surveyCode provided → "account" controller (Auth pattern)
    // - explicit controllerProp overrides auto-detection
    const resolvedController = controllerProp || (formId ? "forms" : "account");
    // The identifier for fetching: formId takes precedence, then surveyCode
    const resolvedIdentifier = formId || surveyCode;

    // ─── Event Handlers ───────────────────────────────────────────────

    // Applies the admin-configured section CSS class to a panel's root element.
    // onUpdatePanelCssClasses fires per-panel but shares the css object, so we
    // rebuild the container class from a captured pristine base each time to
    // avoid one panel's class leaking onto the others.
    const applyPanelCssClass = (options) => {
        const panel = options.panel;
        const cssClasses = options.cssClasses;
        // The panel root (sd-panel div, sits directly under the data-key="section_..." row)
        // is exposed either as cssClasses.panel.container (nested) or cssClasses.container (flat).
        const target = cssClasses.panel || cssClasses;
        const key = "container";

        // Capture the pristine base container class the first time we touch this panel.
        if (target.__baseContainer === undefined) {
            target.__baseContainer = target[key] || "";
        }

        const cls = panel.cssClass
            || (panel.getPropertyValue && panel.getPropertyValue("cssClass"))
            || cssClassMapRef.current[panel.name];

        let containerClass = cls ? (target.__baseContainer + " " + cls).trim() : target.__baseContainer;

        // When a section (panel) has neither a title nor a description, tag it so the
        // separator top line and header spacing can be suppressed via CSS. SurveyJS still
        // renders the header/title wrapper elements even when the text is empty, so a
        // pure-CSS :has() test can't detect this — the marker class is the reliable signal.
        const panelTitle = (panel.title || (panel.getPropertyValue && panel.getPropertyValue("title")) || "").trim();
        const panelDescription = (panel.description || (panel.getPropertyValue && panel.getPropertyValue("description")) || "").trim();
        if (!panelTitle && !panelDescription) {
            containerClass = (containerClass + " section--no-header").trim();
        }

        target[key] = containerClass;
    };

    const renderHelpPanel = (questionElement, helpTitle, helpText) => {
        // Avoid duplicate panels
        if (questionElement.querySelector('.help-panel')) return;

        const panel = document.createElement("div");
        panel.className = "help-panel";
        panel.style.display = "none";

        let panelContent = '';
        if (helpTitle) panelContent += `<p class="help-panel-title">${DOMPurify.sanitize(helpTitle)}</p>`;
        if (helpText) panelContent += `<p class="help-panel-text">${DOMPurify.sanitize(helpText)}</p>`;
        panel.innerHTML = panelContent;

        // Insert panel after the header (childNodes[0])
        const header = questionElement.childNodes[0];
        if (header && header.nextSibling) {
            questionElement.insertBefore(panel, header.nextSibling);
        } else {
            questionElement.appendChild(panel);
        }

        // Wire up toggle on the help icon button
        const helpBtn = questionElement.querySelector('.help-icon-btn');
        if (helpBtn) {
            helpBtn.addEventListener('click', (e) => {
                e.preventDefault();

                // The help button sits inside the header, so its click bubbles to SurveyJS's
                // "click title -> focus first input" handler — suppress that auto-focus below.
                // Exclude the help button (a click focuses it first) when checking whether
                // focus was already inside, else the guard would always match.
                const active = document.activeElement;
                const alreadyFocusedInside =
                    questionElement.contains(active) && !helpBtn.contains(active);
                // Only let the click reach the header (keeping normal focus) if the user was
                // already working inside this question.
                if (!alreadyFocusedInside) {
                    e.stopPropagation();
                }

                const isVisible = panel.style.display !== 'none';
                panel.style.display = isVisible ? 'none' : 'block';
                const icon = helpBtn.querySelector('span');
                if (icon) {
                    icon.className = isVisible ? 'question-icon' : 'close-icon';
                }
            });
        }
    };

    const handleonRender = (sender, options) => {
        // Apply custom CSS class from admin (if configured on the question)
        // First try direct property (works if Serializer registration succeeded),
        // then fall back to the pre-built map from raw JSON.
        const customCssClass = options.question.cssClass 
            || options.question.getPropertyValue("cssClass")
            || cssClassMapRef.current[options.question.name];

        if (customCssClass) {
            // Add to the element — note: may be overwritten by React re-renders
            // The persistent approach uses onUpdateQuestionCssClasses (see below)
            String(customCssClass).split(/\s+/).forEach(cls => {
                if (cls) options.htmlElement.classList.add(cls);
            });
        }

        // Boolean toggle-switch class management
        if (enableBooleanSlider) {
            if (options.question.getType() === 'boolean') {
                const renderAs = options.question.jsonObj?.renderAs || options.question.getPropertyValue("renderAs");
                if (renderAs === 'toggle-switch') {
                    options.htmlElement.classList.add('custom-boolean', 'custom-boolean--toggle');
                } else {
                    options.htmlElement.classList.add('custom-boolean', 'custom-boolean--checkbox');
                }
            }
            if (options.question.getType() === 'checkbox' && options.question.jsonObj?.renderAs === 'toggle-switch') {
                options.htmlElement.classList.add('custom-boolean', 'custom-boolean--toggle');
            }
        }

        // Dynamic endpoint loading
        if (enableEndpoints && apiGet) {
            if (options.question?.jsonObj?.isCountry && options.question?.dataEndpoint) {
                handleEndpoints(options);
            }
            if (options.question?.addItem && options.question?.dataEndpoint) {
                handleEndpoints(options);
            }
            if (options.question.jsonObj?.searchable && options.question.jsonObj?.dataEndpoint) {
                handleEndpoints(options);
            }
            // Also trigger for any dropdown with a dataEndpoint (Site.V2 forms pattern)
            if (options.question.getType() === 'dropdown' && options.question.jsonObj?.dataEndpoint) {
                handleEndpoints(options);
            }

            if (options.question?.jsonObj?.dataEndpoint) {
                options.question.searchEndpoint = (term) => handleEndpoints(options, term);
            }
        }

       //added a fallback for programmatically built questions.
        let required = options.question.jsonObj?.isRequired ?? options.question.isRequired;

        // Dropdown category grouping
        if (options.question.getType() === 'dropdown') {
            const dropdownChoices = options.question.jsonObj?.choicesByCategory;
            const category = options.question.jsonObj?.choices;
            if (dropdownChoices) {
                const dropdownList = options.htmlElement.querySelectorAll("input");

                dropdownList.forEach((list) => {
                    list.addEventListener("focus", () => {
                        setTimeout(() => {
                            const popupList = options.htmlElement.querySelector(".sv-dropdown-popup .sv-list");
                            if (!popupList) return;
                            const listItems = popupList.querySelectorAll("li");
                            const groupContainers = {};
                            const categoryMap = {};

                            category?.forEach((choice) => {
                                let cat = choice.category || "";
                                if (!categoryMap[cat]) {
                                    categoryMap[cat] = [];
                                }
                                categoryMap[cat].push(choice.text);
                            });

                            popupList.innerHTML = "";

                            listItems.forEach((li) => {
                                const inputValue = li.textContent.trim();
                                if (!inputValue) return;

                                const categoryList = Object.keys(categoryMap).find((key) =>
                                    categoryMap[key].includes(inputValue)
                                ) || fallbackLabel;

                                if (!groupContainers[categoryList]) {
                                    const container = document.createElement("div");
                                    container.className = `item-container dropdown-category-group category-group-${categoryList?.replace(/\s+/g, "-").replace(/[^a-zA-Z0-9\-]/g, "")}`;

                                    const header = document.createElement("h5");
                                    header.className = "module-subheading";
                                    header.textContent = categoryList;

                                    const ul = document.createElement("ul");
                                    container.appendChild(header);
                                    container.appendChild(ul);

                                    popupList.appendChild(container);
                                    groupContainers[categoryList] = ul;
                                }

                                groupContainers[categoryList].appendChild(li);
                            });
                        }, 0);
                    });
                });
            }
        }

        // Help icon and panel logic
        const helpTitle = options.question.jsonObj?.helpTitle;
        const helpText = options.question.jsonObj?.helpText;
        const hasHelp = !!(helpTitle || helpText);

        // "Optional" label + help icon injection
        // Skip for HTML questions (display-only, no mandatory/optional concept)
        // Skip display-only elements with no input (e.g. the PADD-3866 step indicator) —
        // they have no mandatory/optional concept, so they must not get an "Optional" tag.
        // Skip DPA questions (consent) — a DPA Yes/No must not be labelled "Optional".
        //   Detected via displayChannelsAs ("single"/"separate", set on both DPA modes) or isDpa.
        // Skip if already rendered (prevents duplicates on re-render)
        const isDpaQuestion = !!(options.question.jsonObj?.displayChannelsAs || options.question.jsonObj?.isDpa);

        // Single-mode DPA checkbox/slider labels its one option with the question title, so the
        // header would duplicate it. Hide the header (titleLocation "hidden" — NOT an empty title,
        // which makes SurveyJS fall back to showing the question name).
        if (isDpaQuestion
            && options.question.getType() === 'checkbox'
            && options.question.jsonObj?.displayChannelsAs === 'single'
            && options.question.titleLocation !== 'hidden') {
            options.question.titleLocation = 'hidden';
        }

        if (options.question.getType() !== 'html'
            && options.question.getType() !== STEP_INDICATOR_TYPE
            && options.question.hasInput !== false
            && !isDpaQuestion
            && !options.htmlElement.querySelector('.optional')
            && !options.htmlElement.querySelector('.label-aid')) {
            const optionalText = t('surveyform.optional');
            const helpAriaLabel = t('surveyform.helparialabel');

            if (!required && !hasHelp) {
                // Simple case: just "Optional" label (matches master pattern)
                const header = options.htmlElement.childNodes[0];
                if (header) {
                    header.innerHTML += `<div class="optional">${optionalText}</div>`;
                }
            } else if (!required || hasHelp) {
                // Complex case: has help icon, use label-aid wrapper
                let labelAidHtml = '<div class="label-aid">';
                if (!required) labelAidHtml += `<span class="optional">${optionalText}</span>`;
                if (!required && hasHelp) labelAidHtml += '<span class="label-aid-separator">|</span>';
                if (hasHelp) labelAidHtml += `<button type="button" class="help-icon-btn" aria-label="${helpAriaLabel}"><span class="question-icon"></span></button>`;
                labelAidHtml += '</div>';

                const header = options.htmlElement.childNodes[0];
                if (header) {
                    header.innerHTML += labelAidHtml;
                }
            }
            if (hasHelp) renderHelpPanel(options.htmlElement, helpTitle, helpText);
        }

        // Blur validation — attach to actual input/textarea elements
        let inlineForms = options.htmlElement.querySelector("input, textarea");
        if (inlineForms) {
            inlineForms.addEventListener("blur", function (event) {
                const { value } = event.target;
                setTimeout(() => {
                    if (!value) {
                        options.question.hasErrors(true);
                    }
                }, 0);
            });
        }

        if (options.question.getType() !== "checkbox" && options.question.getType() !== "radiogroup") return;

        // Skip DOM manipulation for checkbox questions rendered as toggle-switch
        // (the CheckboxSlider custom renderer handles its own layout)
        if (options.question.getType() === 'checkbox' && options.question.jsonObj?.renderAs === 'toggle-switch') return;

        // Skip if category grouping has already been applied (prevents duplication on re-render)
        if (options.htmlElement.querySelector('.category-group')) return;

        // Checkbox/radiogroup category grouping
        let category = options.question.jsonObj?.choices;
        let categoryMap = {};

        category?.forEach((choice) => {
            let cat = choice.category || "";
            if (!categoryMap[cat]) {
                categoryMap[cat] = [];
            }
            categoryMap[cat].push(choice.value);
        });

        let inputElements = options.htmlElement.querySelectorAll("input[type='radio'], input[type='checkbox']");

        inputElements.forEach((inputElement) => {
            let itemValue = inputElement.value;
            let itemParent = inputElement.closest('.sd-item');
            let instertBeforeItem = inputElement.closest('.sd-question__content');
            let categoryList = Object.keys(categoryMap).find((key) =>
                categoryMap[key].includes(itemValue)
            );

            if (categoryList) {
                let groupContainer = options.htmlElement.querySelector(`.category-group-${categoryList?.replace(/\s+/g, "-").replace(/[^a-zA-Z0-9\-]/g, "")}`);

                if (!groupContainer) {
                    groupContainer = document.createElement("div");
                    groupContainer.className = `item-container category-group category-group-${categoryList?.replace(/\s+/g, "-").replace(/[^a-zA-Z0-9\-]/g, "")}`;
                    groupContainer.innerHTML = ` <h5 class="module-subheading">${categoryList}</h5>`;
                    options.htmlElement.insertBefore(groupContainer, instertBeforeItem);
                }

                if (itemParent) {
                    groupContainer.appendChild(itemParent);
                }
            }

            // Newsletter description
            if (options.question.jsonObj?.isNewsletter && options.question.jsonObj?.isOptionDescriptionVisible) {
                let choices = options.question.jsonObj?.choices;
                let matchedChoice = choices?.find(c => c.value === itemValue);
                if (matchedChoice?.description && itemParent) {
                    if (!itemParent.querySelector('.newsletter-option-description')) {
                        let descEl = document.createElement("div");
                        descEl.className = "newsletter-option-description";
                        descEl.innerHTML = DOMPurify.sanitize(matchedChoice.description);
                        itemParent.appendChild(descEl);
                    }
                }
            }
        });
    };

    // Note: the "Other" choice item and its comment area are now paired together by a
    // dedicated custom renderer (see OtherGroupRenderer.js — CheckboxWithOtherGroup /
    // RadiogroupWithOtherGroup, registered as the default renderer for checkbox/radiogroup
    // questions). That renderer wraps both inside a single .sd-other-group div directly in
    // the render output, which survives re-renders — a plain onAfterRenderQuestion DOM
    // patch does not, since SurveyJS/React re-renders the item list independently.

    // NOTE: We intentionally do NOT rewrite question.value (or question.comment) to inject
    // adOtherValue anywhere in this file. Calling sender.setValue()/question.value= on a
    // select-base question re-enters SurveyJS's own other/comment-sync machinery
    // (QuestionSelectBase.setQuestionValue -> updateCommentFromSurvey / setOtherValueInternally),
    // which OVERWRITES the just-typed comment with whatever the new value ends up being — for
    // BOTH single-select (dropdown/radiogroup) and multi-select (checkbox) questions. In other
    // words, calling setValue(field, adOtherValue) does not just swap "other" for the real
    // other-answer code — it also clobbers the typed comment text itself. This was verified
    // directly against survey-core's runtime behaviour, not assumed.
    //
    // So the question's own value is left exactly as SurveyJS produces it natively: the
    // literal string "other" (single-select) or an array containing "other" (checkbox), and
    // the typed comment is left exactly as SurveyJS's own setComment/-Comment mechanism
    // produces it. Instead, the real "other" answer code (adOtherValue, e.g. "1001|other")
    // is exported as a SEPARATE "<field>-OtherCode" key at getSurveyData() time (see
    // getSurveyData below), so the backend can resolve the true Inform answer id without us
    // ever touching SurveyJS's internal value/comment state.

    const handleEndpoints = (options, searchTerm) => {
        if (!apiGet) return Promise.resolve([]);

        const apiProps = {
            "controller": resolvedController,
            "action": options.question.dataEndpoint,
            "identifier": resolvedIdentifier,
            "environment": environment,
            "data": ""
        };
        const q = options.question.jsonObj || options.question;

        if (q?.dataEndpoint === "getformcompanies") {
            //passing email for domain matching
            const userEmail = localStorage.getItem('email') || "";

            apiProps.data = {
                isDisplayAddrLine1: !!q.isDisplayAddrLine1,
                isDisplayPostCode: !!q.isDisplayPostCode,
                isDisplayTown: !!q.isDisplayTown,
                isDisplayCountry: !!q.isDisplayCountry,
                email: userEmail,
                searchTerm: searchTerm || "",
            };
        }

        return apiGet(apiProps)
            .then(function (response) {
                let data = response.data.choices;
                let choices;
                if (q?.dataEndpoint === "getformcompanies") {
                    // AD_CompanyLookupSearch_V3 already applies direct/indirect domain
                    // matching and ordering server-side, so the list is used as-is.
                    choices = data.map((item) => {
                        const addressInfo = [item?.AddressLine1, item?.PostCode, item?.Town, item?.Country].filter(Boolean);
                        const details = addressInfo.length > 0 ? ` (${addressInfo.join(", ")})` : "";
                        return {
                            value: item.Name,
                            text: `${item.Name}${details}`,
                        };
                    });
                } else {
                    choices = data.map((item) => ({
                        value: item.name,
                        text: item.name,
                    }));
                }

                // Preserve a manually-added company through Add New Company feature so a search
                // response can't wipe out the value the user just created.
                const createdValue = options.question.adCreatedValue;
                if (createdValue && !choices.some((c) => c.value === createdValue)) {
                    choices = [...choices, { value: createdValue, text: createdValue }];
                }

                options.question.choices = choices;
                return choices;
            })
            .catch((err) => {
                console.error("API error:", err);
                return [];
            });
    };

    // ─── Initialization ───────────────────────────────────────────────

    const initializeModel = (surveyModel) => {
        // Hide SurveyJS built-in navigation buttons — the host component manages
        // Previous/Next/Submit externally in both single-page and multi-page modes.
        surveyModel.showNavigationButtons = false;

        // Expose apiGet + environment on the model so custom composite questions
        // (e.g. the "addressFieldsFromCountry" question) can make API calls.
        surveyModel.adApiGet = apiGet;
        surveyModel.adEnvironment = environment;
        surveyModel.adController = resolvedController;
        surveyModel.adIdentifier = resolvedIdentifier;
        surveyModel.adUserToken = userToken;
        surveyModel.adPrefillLatestAddress = prefillLatestAddress;

        // Disable SurveyJS's built-in focus-on-first-error. On a failed NEXT/validate it runs
        // SYNCHRONOUSLY inside validate() — it focuses getElementById(question.inputId) BEFORE
        // React re-renders the question into its error state. React then re-renders that
        // question (adding .sd-question--error and, for custom renderers, replacing the input
        // node), which discards the focus SurveyJS just set — so focus landed nowhere on the
        // first click and only "worked" on the second (when the error DOM already existed).
        // The host (CaptureForms / Register) owns focus instead, via its own helper that waits
        // for the error DOM to render before focusing — a single, deferred, reliable mechanism.
        // Property name differs across survey-core versions (focusOnFirstError, legacy
        // focusFirstQuestionAutomatic, newer autoFocusFirstQuestion), so set whichever exist.
        try {
            if ("focusOnFirstError" in surveyModel) surveyModel.focusOnFirstError = false;
            if ("autoFocusFirstError" in surveyModel) surveyModel.autoFocusFirstError = false;
        } catch (e) { /* property not supported in this survey-core build — safe to ignore */ }

        // Paging: when disabled (default), collapse to a single page so legacy consumers
        // are unaffected. When enabled, keep all pages and notify the host on page change
        // so it can render per-page navigation and RHC content.
        if (!enablePaging) {
            keepFirstPageOnly(surveyModel);
        } else if (onPageChanged) {
            surveyModel.onCurrentPageChanged.add((sender) => {
                onPageChanged(sender.currentPageNo);
            });
        }

        // Apply custom CSS classes from admin — persists through re-renders
        surveyModel.onUpdateQuestionCssClasses.add((sender, options) => {
            const cls = options.question.cssClass 
                || options.question.getPropertyValue("cssClass") 
                || cssClassMapRef.current[options.question.name];
            if (cls) {
                options.cssClasses.mainRoot = (options.cssClasses.mainRoot || "") + " " + cls;
            }
            applyDisplayLayoutClass(options);
        });

        // Apply custom CSS classes from admin to sections (panels) — persists through re-renders
        surveyModel.onUpdatePanelCssClasses.add((sender, options) => {
            applyPanelCssClass(options);
        });

        surveyModel.onAfterRenderQuestion.add(handleonRender);

        // Page/section heading tags (h1/h2) are set globally via settings.titleTags above —
        // SurveyJS renders the correct tags directly, so no post-render promotion is needed.

        // Render HTML in question descriptions. The description is admin-authored WYSIWYG
        // HTML from the backend; sanitise it before handing it to SurveyJS as markup so it
        // renders as formatted content instead of escaped text in a span. Only a QUESTION
        // element's description is wrapped in a .question-description div for styling hooks —
        // panel and page descriptions share the same options.name === "description" but must
        // NOT receive this wrapper, so we additionally require options.element to be a question.
        surveyModel.onTextMarkdown.add((sender, options) => {
            const clean = DOMPurify.sanitize(options.text);
            options.html = options.name === "description" && options.element?.isQuestion === true
                ? `<div class="question-description">${clean}</div>`
                : clean;
        });

        // Capture the first page's admin-configured title & description BEFORE hideFirstPagePanel
        // clears them. The host (e.g. Register) uses them for its own heading/intro on a
        // multi-page form.
        firstPageTitleRef.current = surveyModel.pages?.[0]?.title || '';
        firstPageDescriptionRef.current = surveyModel.pages?.[0]?.description || '';
        // Hide first page panel (title + description) when it follows the native registration form
        if (hideFirstPagePanel) {
            const firstPage = surveyModel.pages[0];
            if (firstPage) {
                // Clear only the page-level title and description so the survey blends after
                // the native registration fields. The first panel/section title is genuine
                // admin-configured content and must NOT be cleared.
                firstPage.title = '';
                firstPage.description = '';
            }
        }

        // Ensure preselected boolean toggle switches have their value set
        if (enableBooleanSlider) {
            surveyModel.getAllQuestions().forEach(q => {
                if (q.getType() === "boolean" && q.getPropertyValue("renderAs") === "toggle-switch") {
                    if (q.defaultValue === true || q.getPropertyValue("defaultValue") === true) {
                        surveyModel.setValue(q.name, q.valueTrue || true);
                    }
                }
            });
        }

        //Adding our built questions- "addressFieldsFromCountry" markers to real survey questions
        expandAddressFieldsFromCountry(surveyModel);
        // Override-mode forms: pre-fill standalone address questions (no-op unless
        // prefillLatestAddress is on and the form has addressFieldRole-tagged questions).
        prefillStandaloneAddressFields(surveyModel);

        return surveyModel;
    };

    // Build the branching engine for a model, but only when branching is enabled AND rules
    // exist. For a non-branching form the ref stays null and the imperative nextPage()/
    // prevPage() fall through to plain linear SurveyJS navigation. `rules` can come from the
    // branchingRules prop (Mode 1) or the fetch response (Mode 2/3).
    const setupBranchingEngine = (surveyModel, rules) => {
        if (!enablePaging) return; // branching only matters for multi-page forms
        if (!surveyModel) return;
        if (!rules || Object.keys(rules).length === 0) return;
        branchingEngineRef.current = new BranchingEngine(surveyModel, rules);
    };

    // Mode 1: Pre-built model passed via props
    // Register event handlers BEFORE the first render so onAfterRenderQuestion fires with
    // our handler attached. useEffect runs AFTER render which is too late — the initial
    // questions have already rendered and the event has already fired without our listener.
    if (survey && !initialized.current) {
        // Build cssClass lookup map from the raw model JSON
        cssClassMapRef.current = buildCssClassMap(survey);
        console.log('[CssClass] map built:', cssClassMapRef.current);

        // Hide SurveyJS built-in navigation buttons — the host component manages
        // Previous/Next/Submit externally in both single-page and multi-page modes.
        survey.showNavigationButtons = false;

        // Expose apiGet + environment on the model so custom composite questions
        // (e.g. the "addressFieldsFromCountry" question) can make API calls.
        survey.adApiGet = apiGet;
        survey.adEnvironment = environment;
        survey.adController = resolvedController;
        survey.adIdentifier = resolvedIdentifier;
        survey.adUserToken = userToken;
        survey.adPrefillLatestAddress = prefillLatestAddress;

        // Disable SurveyJS's built-in focus-on-first-error. It runs SYNCHRONOUSLY inside
        // validate()/nextPage() and focuses getElementById(question.inputId) BEFORE React
        // re-renders the question into its error state; the follow-up re-render then discards
        // that focus (so focus landed nowhere on the first click and only "worked" on the
        // second). The host (CaptureForms / Register) owns focus instead, deferring until the
        // error DOM has rendered — a single, reliable mechanism. This is the pre-built-model
        // path (Mode 1, Site.V2 CaptureForms); initializeModel() does the same for fetch modes.
        try {
            if ("focusOnFirstError" in survey) survey.focusOnFirstError = false;
            if ("autoFocusFirstError" in survey) survey.autoFocusFirstError = false;
        } catch (e) { /* property not supported in this survey-core build — safe to ignore */ }

        // Paging: when disabled (default), collapse to a single page so legacy consumers
        // are unaffected. When enabled, keep all pages and notify the host on page change.
        if (!enablePaging) {
            keepFirstPageOnly(survey);
        } else if (onPageChanged) {
            survey.onCurrentPageChanged.add((sender) => {
                onPageChanged(sender.currentPageNo);
            });
        }

        // Apply custom CSS classes from admin — uses onUpdateQuestionCssClasses so it persists through re-renders
        survey.onUpdateQuestionCssClasses.add((sender, options) => {
            const cls = options.question.cssClass 
                || options.question.getPropertyValue("cssClass") 
                || cssClassMapRef.current[options.question.name];
            if (cls) {
                // options.cssClasses.mainRoot is the outer wrapper div (sd-element)
                options.cssClasses.mainRoot = (options.cssClasses.mainRoot || "") + " " + cls;
            }
            applyDisplayLayoutClass(options);
        });

        // Apply custom CSS classes from admin to sections (panels) — persists through re-renders
        survey.onUpdatePanelCssClasses.add((sender, options) => {
            applyPanelCssClass(options);
        });

        survey.onAfterRenderQuestion.add(handleonRender);
        // Page/section heading tags (h1/h2) are set globally via settings.titleTags above —
        // SurveyJS renders the correct tags directly, so no post-render promotion is needed.
        // Render HTML in question descriptions. The description is admin-authored WYSIWYG
        // HTML from the backend; sanitise it before handing it to SurveyJS as markup so it
        // renders as formatted content instead of escaped text in a span. Only a QUESTION
        // element's description is wrapped in a .question-description div for styling hooks —
        // panel and page descriptions share the same options.name === "description" but must
        // NOT receive this wrapper, so we additionally require options.element to be a question.
        survey.onTextMarkdown.add((sender, options) => {
            const clean = DOMPurify.sanitize(options.text);
            options.html = options.name === "description" && options.element?.isQuestion === true
                ? `<div class="question-description">${clean}</div>`
                : clean;
        });
        // Capture the first page's admin-configured title & description BEFORE hideFirstPagePanel
        // clears them. The host (e.g. Register) uses them for its own heading/intro on a
        // multi-page form.
        firstPageTitleRef.current = survey.pages?.[0]?.title || '';
        firstPageDescriptionRef.current = survey.pages?.[0]?.description || '';
        // Hide first page panel (title + description) when it follows the native registration form
        if (hideFirstPagePanel) {
            const firstPage = survey.pages[0];
            if (firstPage) {
                // Clear only the page-level title and description so the survey blends after
                // the native registration fields. The first panel/section title is genuine
                // admin-configured content and must NOT be cleared.
                firstPage.title = '';
                firstPage.description = '';
            }
        }

        //Adding our built questions- "addressFieldsFromCountry" markers to real survey questions
        expandAddressFieldsFromCountry(survey);
        // Override-mode forms: pre-fill standalone address questions (no-op unless
        // prefillLatestAddress is on and the form has addressFieldRole-tagged questions).
        prefillStandaloneAddressFields(survey);

        // Branching (PADD-3742): wire the engine from the branchingRules prop (the host, e.g.
        // CaptureForms, reads it from the API response and passes it in with the pre-built model).
        setupBranchingEngine(survey, branchingRules);
        // PADD-3866: inject the in-survey step indicator (one per page) when paging is on,
        // the form is multi-page, and it does not branch. Rendered by SurveyJS after each
        // page's title, before its questions.
        if (showStepIndicator && enablePaging && !branchingEngineRef.current) {
            injectStepIndicators(survey, stepIndicatorStartPage);
        }
        initialized.current = true;
        if (onReady) {
            onReady({
                pageCount: survey.pageCount,
                currentPageNo: survey.currentPageNo,
                // PADD-3866: lets a host (e.g. Register) hide its step indicator when the
                // form branches. True only when the branching engine was actually wired
                // (non-empty rules); null/empty rules leave navigation linear.
                hasBranching: !!branchingEngineRef.current,
                // The first page's admin-configured title & description (empty strings if none).
                // Lets a multi-page host use them as its page heading and intro text.
                firstPageTitle: firstPageTitleRef.current,
                firstPageDescription: firstPageDescriptionRef.current
            });
        }
    }

    // Sync surveyData state when the survey prop arrives (Mode 1)
    useEffect(() => {
        if (survey && surveyData !== survey) {
            setSurveyData(survey);
        }
    }, [survey]);

    // Mode 2/3: Fetch form by surveyCode or formId
    useEffect(() => {
        if (resolvedIdentifier && apiGet && !survey) {
            const fetchForm = async () => {
                try {
                    const response = await apiGet({
                        "controller": resolvedController,
                        "action": "getformbyid",
                        "identifier": resolvedIdentifier,
                        "environment": environment
                    });
                    let surveyJson = response.data.surveyData;
            
                    const surveyModel = new Model(surveyJson);
                    initializeModel(surveyModel);
                    cssClassMapRef.current = buildCssClassMap(surveyModel);
                    // Branching (PADD-3742): auto-wire the engine from the fetch response's
                    // top-level branchingRules (sibling of surveyData), falling back to any
                    // rules passed in via the prop.
                    setupBranchingEngine(surveyModel, response.data.branchingRules || branchingRules);
                    // PADD-3866: inject the in-survey step indicator (see the Mode 1 note).
                    if (showStepIndicator && enablePaging && !branchingEngineRef.current) {
                        injectStepIndicators(surveyModel, stepIndicatorStartPage);
                    }
                    setSurveyData(surveyModel);
                    initialized.current = true;
                    if (onReady) {
                        onReady({
                            pageCount: surveyModel.pageCount,
                            currentPageNo: surveyModel.currentPageNo,
                            // PADD-3866: see note at the Mode 1 onReady call — true only when
                            // the branching engine was wired from non-empty rules.
                            hasBranching: !!branchingEngineRef.current,
                            // The first page's admin-configured title & description (empty
                            // strings if none).
                            firstPageTitle: firstPageTitleRef.current,
                            firstPageDescription: firstPageDescriptionRef.current
                        });
                    }
                } catch (err) {
                    console.error("Failed to load survey form:", err);
                }
            };
            fetchForm();
        }
    }, [resolvedIdentifier]);

    // onComplete handler
    useEffect(() => {
        if (onComplete && surveyData) {
            surveyData.onComplete.add(sender => onComplete(sender.data));
        }
    }, [onComplete, surveyData]);

    // ─── Imperative Handle ────────────────────────────────────────────

    useImperativeHandle(ref, () => ({
        validateSurvey: () => surveyData?.validate(),
        // Paging: validate only the current page (returns true/false). Used by the host
        // component's NEXT button to validate the active page before advancing.
        validateCurrentPage: () => {
            if (!surveyData) return true;
            return surveyData.validateCurrentPage();
        },
        // PADD-3866: flag a page in the step indicator's error state (e.g. when NEXT is
        // blocked by validation). Pass null to clear. Reads back through the custom
        // "adErrorStep" survey property that the injected indicator element observes.
        setErrorStep: (index) => {
            if (!surveyData) return;
            const value = index === undefined ? null : index;
            // Write the error step onto every injected indicator question. Setting a
            // question's OWN property makes SurveyJS re-render that element's React
            // component — reliably, unlike a survey-level property write.
            surveyData.getAllQuestions().forEach((q) => {
                if (q.getType && q.getType() === STEP_INDICATOR_TYPE) {
                    q.setPropertyValue("adErrorStep", value);
                }
            });
        },
        // Paging: advance to the next page. When a branching engine is active (PADD-3742) it
        // handles validation, conditional page jumps and form-completion; otherwise this falls
        // back to plain linear SurveyJS navigation. Returns false if the move was blocked
        // (validation) or there is nowhere to advance to.
        nextPage: () => {
            if (!surveyData) return false;
            if (branchingEngineRef.current) {
                return branchingEngineRef.current.nextPage();
            }
            if (surveyData.isLastPage) return false;
            // SurveyJS nextPage() validates the current page and returns false when it is
            // invalid (staying on the page). Return that result so the host can detect the
            // block and flag the step indicator's error state (PADD-3866). Previously this
            // always returned true, so a validation-blocked NEXT looked like a successful
            // move and the error "!" never showed on the current step.
            return surveyData.nextPage();
        },
        // Paging: go back to the previous page. With branching active, retraces the exact path
        // taken forward (history-based); otherwise plain linear prevPage. Returns false if
        // already at the start.
        prevPage: () => {
            if (!surveyData) return false;
            if (branchingEngineRef.current) {
                return branchingEngineRef.current.prevPage();
            }
            if (surveyData.isFirstPage) return false;
            surveyData.prevPage();
            return true;
        },
        // Paging state queries for the host component's navigation rendering.
        isFirstPage: () => surveyData?.isFirstPage ?? true,
        isLastPage: () => surveyData?.isLastPage ?? true,
        pageCount: () => surveyData?.pageCount ?? 1,
        currentPageNo: () => surveyData?.currentPageNo ?? 0,
        getSurveyData: () => {
            if (!surveyData) return surveyData?.data;
            const data = { ...surveyData.data };
            // DPA radio "No": the backend gives "No" a non-empty value ("no") so it paints as
            // selected (an empty radio value collides with SurveyJS's unset state). A "No" still
            // means "no answer" for Inform, so blank it here before posting — the single place both
            // apps cross to the backend, so the controllers stay untouched.
            surveyData.getAllQuestions().forEach(q => {
                if (q.getType() === "radiogroup" && q.jsonObj?.displayChannelsAs && data[q.name] === "no") {
                    data[q.name] = "";
                }
            });
            // For every "Other"-enabled select question that currently has "other"
            // selected, export the real Inform other-answer code (adOtherValue, e.g.
            // "1001|other") under a separate "<field>-OtherCode" key. The question's own
            // value/comment are left untouched (see handleValueChanged note above) so the
            // backend must read this key to resolve the true answer id instead of relying
            // on the literal "other" placeholder that ships in data[field].
            surveyData.getAllQuestions().forEach(q => {
                if (!q.isOtherSelected) return;
                if (q.adOtherValue) {
                    data[q.name + "-OtherCode"] = q.adOtherValue;
                }
                // When the question has a linked demographic configured, export its dsm_code
                // so the middleware can save the typed "Other" free-text (data[q.name +
                // "-Comment"]) to that separate custom attribute instead of only onto the
                // question itself. See AccountController.saveDemographics.
                if (q.adOtherLinkedCode) {
                    data[q.name + "-OtherLinkedCode"] = q.adOtherLinkedCode;
                }
            });

            // Three-dropdown DATE questions (QUESTIONTYPE 13, renderAs "date-dropdown") hold a
            // single "yyyy-MM-dd" value, but the legacy Inform DATE control expects three
            // separate integer sub-fields (Day / Month / Year), each posted under its own
            // answer code: THREEDDLDATE_{key}_0_{code}. Here we split the value into integer
            // parts and export one companion key per part — "{name}-DatePart-{code}" = integer —
            // so the backend transform rebuilds the three fields without needing the schema at
            // submit time. The original combined "{name}" value is removed so it is not posted
            // as a bogus single field. Day/Month are NOT zero-padded, matching Inform's integer
            // <option value="1".."31"> list.
            surveyData.getAllQuestions().forEach(q => {
                const renderAs = q.jsonObj?.renderAs || q.getPropertyValue?.("renderAs") || q.renderAs;
                if (renderAs !== "date-dropdown") return;
                const codes = [
                    q.jsonObj?.dayCode || q.getPropertyValue?.("dayCode"),
                    q.jsonObj?.monthCode || q.getPropertyValue?.("monthCode"),
                    q.jsonObj?.yearCode || q.getPropertyValue?.("yearCode"),
                ];
                const raw = data[q.name];
                const m = typeof raw === "string" && /^(\d{4})-(\d{2})-(\d{2})$/.exec(raw);
                // Only export when the date is complete AND all three codes are configured;
                // otherwise leave the data untouched (an incomplete/partial date is dropped,
                // same as the renderer never writing a partial value to q.value).
                if (m && codes[0] && codes[1] && codes[2]) {
                    const year = String(Number(m[1]));
                    const month = String(Number(m[2]));
                    const day = String(Number(m[3]));
                    const parts = [day, month, year];
                    delete data[q.name];
                    codes.forEach((code, i) => {
                        data[`${q.name}-DatePart-${code}`] = parts[i];
                    });
                }
            });
            return data;
        },
        getBooleanToggleValues: () => {
            if (!surveyData) return {};
            const result = {};
            const questions = surveyData.getAllQuestions();
            questions.forEach(q => {
                if (q.getType() === "boolean") {
                    const renderAs = q.getPropertyValue("renderAs") || q.jsonObj?.renderAs || q.renderAs;
                    if (renderAs === "toggle-switch") {
                        const hasExplicitValue = q.value !== undefined && q.value !== null;
                        let isOn = false;
                        if (hasExplicitValue) {
                            isOn = q.value === true || q.value === q.valueTrue;
                        } else {
                            isOn = q.defaultValue === true;
                        }
                        if (isOn) {
                            result[q.name] = q.valueTrue || "true";
                        }
                    }
                }
            });
            return result;
        },
        prefixAnswers: () => {
            if (!surveyData) return;
            const survey = surveyData;
            const questions = survey.getAllQuestions();

            questions.forEach(q => {
                if (q.name.startsWith("answercode_")) {
                    q.page.removeQuestion(q);
                }
            });

            questions.forEach((q) => {
                if (!q.isVisible) return;
                if (
                    ["text", "comment", "date", "datetime", "dropdown"].includes(q.getType()) &&
                    q.value &&
                    q.answerCode
                ) {
                    if (q.answerCode && q.value && ["string", "number"].includes(typeof q.value)) {
                        const page = q.page;
                        const hiddenQuestion = new QuestionTextModel("answercode_" + q.name);
                        hiddenQuestion.defaultValue = q.answerCode || "";
                        hiddenQuestion.visible = false;

                        page.addQuestion(hiddenQuestion);
                        survey.setValue(hiddenQuestion.name, hiddenQuestion.defaultValue);
                        console.log(survey)
                    }
                }
            });
        }
    }));



    if (!surveyData) {
        return (
            <div className="survey-loader-wrapper">
                <div className="survey-loader"></div>
            </div>
        );
    }

    return (
        <Survey model={surveyData} />
    );
});

export default SurveyForm;
